import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { useLocation, usePrevious } from 'react-use';

import { ProductShape } from '@repo/ui-components/dept/types';
import {
  FETCH_PRODUCT_UPDATE_QUERY,
  makeShopifyRequest,
} from '@repo/ui-components/dept/utils/shopifyClient';
import { calculatePercentageSavings } from '@repo/utils';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { CartContext } from './CartContext';
import { BazaarVoiceContext } from './BazaarVoiceContext';

const ProductContainerContext = createContext(false);

function ProductContainerProvider({
  children,
  product,
  activeSku,
  onOptionChange,
}) {
  const location = useLocation();
  const { localizePrice } = useUiComponentsInternationalContext();
  const { shopifyClient } = useContext(CartContext);
  const { showBazaarVoice } = useContext(BazaarVoiceContext);
  const { locale } = useUiComponentsInternationalContext();

  const [variants, setVariants] = useState(product?.shopifyProduct?.variants);
  const [activeVariant, setActiveVariant] = useState(null);
  const [updatedProduct, setUpdatedProduct] = useState(null);
  const [sellingPlanGroups, setSellingPlanGroups] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [lastSelectedOption, setLastSelectedOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [subscribed, setSubscribed] = useState(false);
  const [selectedSellingPlan, setSelectedSellingPlan] = useState(null);
  const [addOns, setAddOns] = useState([]);
  const [productLifecycle, setProductLifecycle] = useState(null);

  const isBazaarVoiceEnabled = useMemo(
    () => showBazaarVoice(locale),
    [locale, showBazaarVoice]
  );

  const handleLifecycle = shopifyProduct => {
    const lifecycle =
      shopifyProduct?.productLifecycle?.value ||
      shopifyProduct?.productLifecycle;

    if (lifecycle !== 'Available') {
      return setProductLifecycle(lifecycle);
    }

    const productVariants = Array.isArray(shopifyProduct?.variants)
      ? shopifyProduct?.variants
      : shopifyProduct?.variants?.variants;

    if (productVariants?.length > 1) {
      const variantLifecycle =
        activeVariant?.variantLifecycle?.value ||
        activeVariant?.variantLifecycle;
      if (variantLifecycle !== 'Available')
        return setProductLifecycle(variantLifecycle);
    }

    return setProductLifecycle('Available');
  };

  // TODO: Make the colors into swatches when we can access that data
  // Put the color options in front
  const productOptions = product.shopifyProduct?.options
    ?.filter(o => !o.name.toLowerCase().includes('title'))
    .reduce((acc, element) => {
      if (element.name.toLowerCase().includes('color')) {
        return [element, ...acc];
      }
      return [...acc, element];
    }, []);

  const handleOptionChange = ({ target: { name, value } }) => {
    const newSelectedOptions = {
      ...selectedOptions,
      [name]: value,
    };

    // Try to find exact match including other options already set
    const newVariant = product.shopifyProduct?.variants.find(variant =>
      // And all the selected option properties (ie size, color, etc)
      Object.entries(newSelectedOptions).every(([n, v]) => {
        // Find the option on this variant that relates to the selected option we are currently checking
        const relatedOption = variant.selectedOptions.find(o => o.name === n);
        // If the values are equal for every one of the selected options, this is our new active variant
        return relatedOption.value === v;
      })
    );

    setLastSelectedOption(name);
    setSelectedOptions(newSelectedOptions);
    onOptionChange({
      variant: newVariant,
      options: newSelectedOptions,
    });
  };

  const prevProduct = usePrevious(product);
  useEffect(() => {
    (async () => {
      if (!product.shopifyProduct) return;

      let p = updatedProduct;
      if (
        product?.shopifyProduct?.shopifyId &&
        !isEqual(product, prevProduct)
      ) {
        p = await makeShopifyRequest(
          FETCH_PRODUCT_UPDATE_QUERY,
          {
            id: product.shopifyProduct.shopifyId,
          },
          shopifyClient
        );

        // Set the default selling plan
        if (p?.product?.sellingPlanGroups?.nodes?.length) {
          const groups =
            p.product.sellingPlanGroups.nodes?.[0]?.sellingPlans?.nodes.map(
              plan => {
                const variantWithPriceAdjustment =
                  p.product.variants?.nodes.find(
                    n => !!n.sellingPlanAllocations?.nodes?.length
                  );
                const allocation =
                  variantWithPriceAdjustment.sellingPlanAllocations.nodes.find(
                    spa => spa.sellingPlan.id === plan.id
                  );

                return {
                  ...plan,
                  percentageDiscount:
                    allocation.sellingPlan?.priceAdjustments?.[0]
                      ?.adjustmentValue?.adjustmentPercentage,
                  price: allocation?.priceAdjustments?.[0]?.price?.amount,
                };
              }
            );

          setSellingPlanGroups(groups);
          setSelectedSellingPlan(groups[0]);
          setSubscribed(true);
        }

        setUpdatedProduct(p);
      }

      const mergedVariants = product.shopifyProduct?.variants.map(shopV => {
        const {
          media,
          compareAtPrice,
          selectedOptions: variantOptions,
          shopifyId,
          sku,
          price,
          title,
          variantLifecycle,
          language,
        } = shopV;

        const sanityVariant = product.store.variants?.find(
          v => v.sku === shopV.sku
        );

        let variant = {
          media,
          title,
          compareAtPrice,
          variantOptions,
          shopifyId,
          sku,
          metafields: sanityVariant?.metafields,
          variantLifecycle: variantLifecycle || null,
          price,
          language,
        };

        if (p) {
          const updatedVariant = p?.product?.variants?.nodes?.find(
            v => v.sku === shopV.sku
          );

          if (updatedVariant) {
            const {
              availableForSale,
              quantityAvailable,
              sellingPlanAllocations,
            } = updatedVariant;

            variant = {
              ...variant,
              availableForSale,
              quantityAvailable,
              sellingPlanAllocations,
            };
          }
        }
        return variant;
      });

      if (!isEqual(mergedVariants, variants)) {
        setVariants(mergedVariants);
      }
    })();
  }, [product]);

  useEffect(() => {
    if (updatedProduct) {
      if (activeSku) {
        const thisVariant = variants.find(v => v.sku === activeSku);

        const newSelectedOptions =
          thisVariant?.variantOptions?.reduce(
            (so, o) => ({ [o.name]: o.value, ...so }),
            {}
          ) ||
          thisVariant?.selectedOptions?.reduce(
            (so, o) => ({ [o.name]: o.value, ...so }),
            {}
          ) ||
          {};

        if (
          quantity > thisVariant?.quantityAvailable &&
          thisVariant.quantityAvailable > 0
        ) {
          setQuantity(thisVariant.quantityAvailable);
        }

        if (!isEqual(thisVariant, activeVariant)) {
          setActiveVariant(thisVariant);
        }
        if (!isEqual(newSelectedOptions, selectedOptions)) {
          setSelectedOptions(newSelectedOptions);
        }
      } else {
        setActiveVariant(null);
      }
    }
  }, [product, location, updatedProduct, variants, activeSku]);

  useEffect(() => {
    handleLifecycle(product.shopifyProduct);
  }, [product, activeVariant]);

  const getPriceRange = () => {
    if (!product?.shopifyProduct?.priceRangeV2) return activeVariant?.price;
    const min = parseFloat(
      product?.shopifyProduct?.priceRangeV2?.minVariantPrice.amount
    );
    const max = parseFloat(
      product?.shopifyProduct?.priceRangeV2?.maxVariantPrice.amount
    );

    return min === max
      ? localizePrice(min)
      : `${localizePrice(min)} - ${localizePrice(max)}`;
  };

  const getPricingData = () => {
    const compareAtPrice = activeVariant?.compareAtPrice;

    const unSubPrice =
      activeVariant?.price ??
      product.shopifyProduct?.priceRangeV2?.minVariantPrice?.amount;

    let price =
      activeVariant?.price ??
      product.shopifyProduct?.priceRangeV2?.minVariantPrice?.amount;

    if (activeVariant && subscribed && selectedSellingPlan) {
      const plan = activeVariant.sellingPlanAllocations?.nodes.find(
        n => n.sellingPlan.id === selectedSellingPlan.id
      );
      if (plan?.priceAdjustments) {
        const planPrice = plan.priceAdjustments.find(pa => pa?.price?.amount);
        price = Number(planPrice.price.amount);
      }
    }

    return {
      price,
      compareAtPrice,
      getPriceRange,
      percentageDiscount: calculatePercentageSavings({
        compareAtPrice:
          compareAtPrice && compareAtPrice !== 0 ? compareAtPrice : unSubPrice,
        price,
      }),
      unSubPrice,
    };
  };

  const memoizedValue = useMemo(
    () => ({
      activeVariant,
      productOptions,
      handleOptionChange,
      selectedOptions,
      lastSelectedOption,
      quantity,
      setQuantity,
      product: product?._id ? product : null,
      variants,
      sellingPlanGroups,
      subscribed,
      setSubscribed,
      selectedSellingPlan,
      setSelectedSellingPlan,
      pricing: getPricingData(),
      addOns,
      setAddOns,
      productLifecycle,
      isBazaarVoiceEnabled,
    }),
    [
      product,
      selectedOptions,
      activeVariant,
      quantity,
      variants,
      activeSku,
      subscribed,
      selectedSellingPlan,
      addOns,
      productLifecycle,
      isBazaarVoiceEnabled,
    ]
  );

  return (
    <ProductContainerContext.Provider value={memoizedValue}>
      {children}
    </ProductContainerContext.Provider>
  );
}

ProductContainerProvider.defaultProps = {
  activeSku: null,
};

ProductContainerProvider.propTypes = {
  children: PropTypes.node.isRequired,
  activeSku: PropTypes.string,
  product: ProductShape.isRequired,
  onOptionChange: PropTypes.func.isRequired,
};

export { ProductContainerContext, ProductContainerProvider };
